import { MouseEvent, useState } from 'react';
import { ReactComponent as MoreIcon } from '@elementary/common-ui/assets/more.svg';
import { ReactComponent as AiIcon } from '@elementary/common-ui/assets/ai-generate-magenta.svg';
import { ReactComponent as SeverityIcon } from '@elementary/common-ui/assets/16x16/severity.svg';
import { ReactComponent as StatusIcon } from '@elementary/common-ui/assets/20x20/incident-open.svg';
import { ReactComponent as UserIcon } from '@elementary/common-ui/assets/16x16/user.svg';
import { ReactComponent as ArrowRightIcon } from '@elementary/common-ui/assets/arrow-right.svg';
import { ReactComponent as LinkIcon } from '@elementary/common-ui/assets/link.svg';
import { IconButton, SvgIcon } from '@elementary/common-ui/components';
import { Option } from '@elementary/common-ui/components/Menu/Menu';
import {
  Box,
  Divider,
  Menu as MuiMenu,
  MenuItem,
  Typography,
} from '@mui/material';
import styled from 'styled-components';
import { NO_ASSIGNEE_VALUE } from 'incidents/constants';
import IncidentTicketCreateDialog from 'incidents/components/IncidentTicketCreateDialog';
import ConnectTicketSearchPanel from 'shared/components/ConnectTicketDropdown/ConnectTicketSearchPanel';
import type { IncidentListItem, IncidentTicketRow } from 'incidents/types';
import IncidentTicketList from 'incidents/components/IncidentTicketList';
import UnlinkIncidentTicketDialog from 'incidents/components/UnlinkIncidentTicketDialog';
import useIncidentTicketing from 'incidents/hooks/useIncidentTicketing';
import useIncidentTickets from 'incidents/hooks/useIncidentTickets';
import { SEVERITY_DISPLAY, STATUS_DISPLAY } from 'incidents/constants';
import { radius, space } from '@elementary/common-ui/style/utils';

const IconButtonStyled = styled(IconButton)`
  padding: ${space('smMinus')};
`;

const NestedMenuItem = styled(MenuItem)<{ $active?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${space('sm')};
  min-width: 150px;
  padding: ${space('sm', 'lg')};
  background: ${({ theme, $active }) =>
    $active ? theme.palette.surface.tinted : 'transparent'};

  &:hover {
    background: ${({ theme }) => theme.palette.surface.tinted};
  }
`;

const MenuItemContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${space('sm')};
`;

const SubmenuItem = styled(MenuItem)`
  padding: ${space('sm', 'lg')};
  min-height: 36px;
  border-radius: ${radius('lg')};
  margin: ${space(0, 'xs')};

  &:hover {
    background: ${({ theme }) => theme.palette.surface.tinted};
  }
`;

const LinkedTicketsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: ${space('sm')};
  padding: ${space('sm', 'sm', 0)};
  width: 320px;
`;

const LinkedTicketsDivider = styled(Divider)`
  margin-top: ${space('sm')};
`;

type Props = {
  incident: IncidentListItem;
  isAiIncidentsEnabled?: boolean;
  severityOptions?: Option[];
  statusOptions?: Option[];
  assigneeOptions?: Option[];
  disabled?: boolean;
  onChange?: (
    id: string,
    val: {
      assignee?: string;
      status?: string;
      severity?: string;
    },
  ) => void;
  onOpenAIChat: (
    id: string,
    startMessage: string,
    displayLabel: string,
  ) => void;
};

const IncidentActionsColumnMenu = ({
  incident,
  isAiIncidentsEnabled,
  severityOptions,
  statusOptions,
  assigneeOptions,
  disabled,
  onChange,
  onOpenAIChat,
}: Props) => {
  const [mainMenuAnchor, setMainMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [severityMenuAnchor, setSeverityMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(
    null,
  );
  const [assigneeMenuAnchor, setAssigneeMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [ticketsMenuAnchor, setTicketsMenuAnchor] =
    useState<HTMLElement | null>(null);
  const [aiMenuAnchor, setAiMenuAnchor] = useState<HTMLElement | null>(null);
  const [ticketToUnlink, setTicketToUnlink] =
    useState<IncidentTicketRow | null>(null);

  const {
    selectedEnvId,
    enabledIntegrations,
    ticketDialogOpen,
    selectedIntegration,
    onCreateTicketClick,
    handleDialogConfirm,
    handleDialogClose,
    isCreatingTicket,
    dialogError,
    incidentUrl,
    isResolved,
    attachTicket,
    isAttaching,
    unlinkTicket,
    isUnlinking,
  } = useIncidentTicketing({
    incidentId: incident.id,
    status: incident.status,
  });

  const { data: linkedTickets = [] } = useIncidentTickets({
    incidentId: incident.id,
    enabled: Boolean(mainMenuAnchor),
  });

  const integrations = enabledIntegrations ?? [];
  const canManageTickets =
    !!selectedEnvId && integrations.length > 0 && !isResolved;
  const hasLinkedTickets = linkedTickets.length > 0;
  const showTicketsSubmenu = canManageTickets;
  const canUnlinkTicket = !isResolved;

  const handleAiSuggestionClick = (suggestion: string) => {
    onOpenAIChat(incident.id, suggestion, incident.title);
    handleMainMenuClose();
  };

  const handleMainMenuOpen = (e: MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setMainMenuAnchor(e.currentTarget);
  };

  const handleMainMenuClose = () => {
    setMainMenuAnchor(null);
    setSeverityMenuAnchor(null);
    setStatusMenuAnchor(null);
    setAssigneeMenuAnchor(null);
    setTicketsMenuAnchor(null);
    setAiMenuAnchor(null);
  };

  const handleSeverityChange = (severity: string) => {
    onChange?.(incident.id, {
      severity: SEVERITY_DISPLAY[severity.toLowerCase()] ?? severity,
    });
    handleMainMenuClose();
  };

  const handleStatusChange = (status: string) => {
    onChange?.(incident.id, {
      status: STATUS_DISPLAY[status.toLowerCase()] ?? status,
    });
    handleMainMenuClose();
  };

  const handleAssigneeChange = (assignee: string) => {
    onChange?.(incident.id, {
      assignee:
        assigneeOptions?.find((o) => o.value === assignee)?.value ||
        NO_ASSIGNEE_VALUE,
    });
    handleMainMenuClose();
  };

  const isActionDisabled = disabled || incident.status === 'RESOLVED';
  const showActionsSubmenu = !isActionDisabled && !!onChange;

  const closeOtherSubmenusExcept = (
    exclude: 'severity' | 'assignee' | 'status' | 'tickets' | 'ai',
  ) => {
    if (exclude !== 'severity') setSeverityMenuAnchor(null);
    if (exclude !== 'assignee') setAssigneeMenuAnchor(null);
    if (exclude !== 'status') setStatusMenuAnchor(null);
    if (exclude !== 'tickets') setTicketsMenuAnchor(null);
    if (exclude !== 'ai') setAiMenuAnchor(null);
  };

  const hasMenuContent =
    showActionsSubmenu || showTicketsSubmenu || Boolean(isAiIncidentsEnabled);

  if (!selectedEnvId || !hasMenuContent) {
    return null;
  }

  const showDividerAfterActions =
    showActionsSubmenu && (showTicketsSubmenu || !!isAiIncidentsEnabled);

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <IconButtonStyled onClick={handleMainMenuOpen}>
        <MoreIcon />
      </IconButtonStyled>

      <MuiMenu
        anchorEl={mainMenuAnchor}
        open={Boolean(mainMenuAnchor)}
        onClose={handleMainMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {showActionsSubmenu ? (
          <NestedMenuItem
            $active={Boolean(severityMenuAnchor)}
            onMouseEnter={(e) => {
              setSeverityMenuAnchor(e.currentTarget);
              closeOtherSubmenusExcept('severity');
            }}
          >
            <MenuItemContent>
              <SvgIcon width={16}>
                <SeverityIcon />
              </SvgIcon>
              <Typography>Set severity</Typography>
            </MenuItemContent>
            <SvgIcon width={12} strokeColor="icon.tertiary">
              <ArrowRightIcon />
            </SvgIcon>
          </NestedMenuItem>
        ) : null}

        {showActionsSubmenu ? (
          <NestedMenuItem
            $active={Boolean(assigneeMenuAnchor)}
            onMouseEnter={(e) => {
              setAssigneeMenuAnchor(e.currentTarget);
              closeOtherSubmenusExcept('assignee');
            }}
          >
            <MenuItemContent>
              <SvgIcon width={16}>
                <UserIcon />
              </SvgIcon>
              <Typography>Set assignee</Typography>
            </MenuItemContent>
            <SvgIcon width={12} strokeColor="icon.tertiary">
              <ArrowRightIcon />
            </SvgIcon>
          </NestedMenuItem>
        ) : null}

        {showActionsSubmenu ? (
          <NestedMenuItem
            $active={Boolean(statusMenuAnchor)}
            onMouseEnter={(e) => {
              setStatusMenuAnchor(e.currentTarget);
              closeOtherSubmenusExcept('status');
            }}
          >
            <MenuItemContent>
              <SvgIcon width={16}>
                <StatusIcon />
              </SvgIcon>
              <Typography>Set status</Typography>
            </MenuItemContent>
            <SvgIcon width={12} strokeColor="icon.tertiary">
              <ArrowRightIcon />
            </SvgIcon>
          </NestedMenuItem>
        ) : null}

        {showDividerAfterActions ? <Divider sx={{ my: 1 }} /> : null}

        {showTicketsSubmenu ? (
          <NestedMenuItem
            $active={Boolean(ticketsMenuAnchor)}
            onMouseEnter={(e) => {
              setTicketsMenuAnchor(e.currentTarget);
              closeOtherSubmenusExcept('tickets');
            }}
          >
            <MenuItemContent>
              <SvgIcon width={16} fillColor="icon.tertiary">
                <LinkIcon />
              </SvgIcon>
              <Typography>Tickets</Typography>
            </MenuItemContent>
            <SvgIcon width={12} strokeColor="icon.tertiary">
              <ArrowRightIcon />
            </SvgIcon>
          </NestedMenuItem>
        ) : null}

        {isAiIncidentsEnabled ? (
          <NestedMenuItem
            $active={Boolean(aiMenuAnchor)}
            onMouseEnter={(e) => {
              setAiMenuAnchor(e.currentTarget);
              closeOtherSubmenusExcept('ai');
            }}
          >
            <MenuItemContent>
              <SvgIcon width={16}>
                <AiIcon />
              </SvgIcon>
              <Typography>Investigate with AI</Typography>
            </MenuItemContent>
            <SvgIcon width={12} strokeColor="icon.tertiary">
              <ArrowRightIcon />
            </SvgIcon>
          </NestedMenuItem>
        ) : null}
      </MuiMenu>

      <MuiMenu
        anchorEl={severityMenuAnchor}
        open={Boolean(severityMenuAnchor)}
        onClose={() => setSeverityMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableAutoFocus
        disableEnforceFocus
        slotProps={{
          root: { style: { pointerEvents: 'none' } },
          paper: { style: { pointerEvents: 'auto' } },
        }}
        MenuListProps={{
          onMouseLeave: () => setSeverityMenuAnchor(null),
        }}
      >
        {severityOptions?.map((option) => (
          <SubmenuItem
            key={option.value}
            onClick={() => handleSeverityChange(option.value)}
          >
            {option.icon && <SvgIcon width={16}>{option.icon}</SvgIcon>}
            <Typography sx={{ ml: option.icon ? 1 : 0 }}>
              {option.label}
            </Typography>
          </SubmenuItem>
        ))}
      </MuiMenu>

      <MuiMenu
        anchorEl={assigneeMenuAnchor}
        open={Boolean(assigneeMenuAnchor)}
        onClose={() => setAssigneeMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableAutoFocus
        disableEnforceFocus
        slotProps={{
          root: { style: { pointerEvents: 'none' } },
          paper: { style: { pointerEvents: 'auto', maxHeight: 300 } },
        }}
        MenuListProps={{
          onMouseLeave: () => setAssigneeMenuAnchor(null),
        }}
      >
        <SubmenuItem onClick={() => handleAssigneeChange(NO_ASSIGNEE_VALUE)}>
          <Typography>No assignee</Typography>
        </SubmenuItem>
        {assigneeOptions
          ?.filter((o) => o.value !== NO_ASSIGNEE_VALUE)
          .map((option) => (
            <SubmenuItem
              key={option.value}
              onClick={() => handleAssigneeChange(option.value)}
            >
              <Typography>{option.label}</Typography>
            </SubmenuItem>
          ))}
      </MuiMenu>

      <MuiMenu
        anchorEl={statusMenuAnchor}
        open={Boolean(statusMenuAnchor)}
        onClose={() => setStatusMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableAutoFocus
        disableEnforceFocus
        slotProps={{
          root: { style: { pointerEvents: 'none' } },
          paper: { style: { pointerEvents: 'auto' } },
        }}
        MenuListProps={{
          onMouseLeave: () => setStatusMenuAnchor(null),
        }}
      >
        {statusOptions?.map((option) => (
          <SubmenuItem
            key={option.value}
            onClick={() => handleStatusChange(option.value)}
          >
            {option.icon && <SvgIcon width={16}>{option.icon}</SvgIcon>}
            <Typography sx={{ ml: option.icon ? 1 : 0 }}>
              {option.label}
            </Typography>
          </SubmenuItem>
        ))}
      </MuiMenu>

      <MuiMenu
        anchorEl={ticketsMenuAnchor}
        open={Boolean(ticketsMenuAnchor)}
        onClose={() => setTicketsMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableAutoFocus
        disableEnforceFocus
        slotProps={{
          root: { style: { pointerEvents: 'none' } },
          paper: { style: { pointerEvents: 'auto', overflow: 'visible' } },
        }}
        MenuListProps={{
          onMouseLeave: () => setTicketsMenuAnchor(null),
          sx: { padding: 0 },
        }}
      >
        <Box sx={{ p: 0 }} onMouseDown={(e) => e.stopPropagation()}>
          {hasLinkedTickets ? (
            <>
              <LinkedTicketsContainer>
                <IncidentTicketList
                  tickets={linkedTickets}
                  onUnlink={canUnlinkTicket ? setTicketToUnlink : undefined}
                />
              </LinkedTicketsContainer>
              {canManageTickets ? <LinkedTicketsDivider /> : null}
            </>
          ) : null}
          {canManageTickets ? (
            <ConnectTicketSearchPanel
              envId={selectedEnvId!}
              enabledIntegrations={integrations}
              isActive={Boolean(ticketsMenuAnchor)}
              isAttaching={isAttaching}
              isCreatingTicket={isCreatingTicket}
              onSelect={async (integration, ticket) => {
                await attachTicket({
                  ticketingIntegrationId: integration.id,
                  ticket,
                });
              }}
              onCreateClick={(integration) =>
                onCreateTicketClick(integration.id)
              }
              onClose={handleMainMenuClose}
            />
          ) : null}
        </Box>
      </MuiMenu>

      <MuiMenu
        anchorEl={aiMenuAnchor}
        open={Boolean(aiMenuAnchor)}
        onClose={() => setAiMenuAnchor(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disableAutoFocus
        disableEnforceFocus
        slotProps={{
          root: { style: { pointerEvents: 'none' } },
          paper: { style: { pointerEvents: 'auto' } },
        }}
        MenuListProps={{
          onMouseLeave: () => setAiMenuAnchor(null),
        }}
      >
        <SubmenuItem
          onClick={() => handleAiSuggestionClick('Find the root cause')}
        >
          <Typography>Find the root cause</Typography>
        </SubmenuItem>
        <SubmenuItem
          onClick={() => handleAiSuggestionClick('Show downstream impact')}
        >
          <Typography>Show downstream impact</Typography>
        </SubmenuItem>
      </MuiMenu>

      {ticketDialogOpen && selectedIntegration && selectedEnvId ? (
        <IncidentTicketCreateDialog
          open={ticketDialogOpen}
          onClose={handleDialogClose}
          onConfirm={handleDialogConfirm}
          incidentId={incident.id}
          integration={selectedIntegration}
          envId={selectedEnvId}
          incidentUrl={incidentUrl}
          isCreating={isCreatingTicket}
          error={dialogError}
        />
      ) : null}

      {ticketToUnlink ? (
        <UnlinkIncidentTicketDialog
          ticket={ticketToUnlink}
          isUnlinking={isUnlinking}
          onClose={() => setTicketToUnlink(null)}
          onConfirm={unlinkTicket}
        />
      ) : null}
    </Box>
  );
};

export default IncidentActionsColumnMenu;
